import React, { useState, useEffect, useRef } from "react";
import {
  DownloadBulkRegistrationTemplate,
  getFileProcessRecords,
  SaveBulkRegistrationForPets,
} from "../../../api/apimethods";
import FileSaver from "file-saver";
import UploadPetRegistration from "../../Upload/UploadPetRegistration";
import FileProcessDashboard from "../../DashBoard/Microchip/Pets/FileProcessDashboard";
import { IFileProcessModel } from "../../../typings/models/FileProcessModel";
import { defaultAppContextData } from "../../../services/appData";
import { useContextService } from "../../../services";
import { IPetRegistraionBulkRegRequest } from "../../../typings/interfaces/Requests/PetRegistrationBulkRequestData";
import { AnimalType } from "../../../typings/models/AnimalType";
import Loader from '../../../components/molecules/Loader';
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

const PetsBulkRegistration: React.FC = () => {
  const { getContextValueFromStorage } = useContextService();
  const [contextData] = useState(() =>
    getContextValueFromStorage(defaultAppContextData)
  );
  const user = contextData.user;
  const [certificateNumber, setCertificateNumber] = useState("");
  const [animalType, setAnimalType] = useState(0);
  const [showUpload, setShowUpload] = useState(true);
  const [isSubmiting, setSubmiting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [errorNilMessage, setErrorNilMessage] = useState("");
  const [data, setData] = useState<IFileProcessModel[]>([]);
  const [loginType, setLoginType] = useState("SingPass");
  const isMounted = useRef(true);
  const MySwal = withReactContent(Swal);
  
  useEffect(() => {
    if (user !== undefined) {
      if (user.role != undefined) setLoginType(user.role);
    }
  });

  useEffect(() => {
    isMounted.current = true;
    getFileProcessData();
    return () => { isMounted.current = false; };
  }, []);

  const getFileProcessData = async () => {
  try {
    setSubmiting(true);
    const response = await getFileProcessRecords("Pets", user.uen);
    if (isMounted.current) {
      setData(response.data);
      setSubmiting(false);
    }
  } catch (error) {
    if (isMounted.current) setSubmiting(false);
    showErrorMessage("An error has occurred. Please try again later.");
  }
};

const showErrorMessage = (errorMessage: string | undefined): void => {
  Swal.fire({
    title: 'Error!',
    text: errorMessage || 'Something went wrong, please try again.',
    icon: 'error',
    confirmButtonText: 'Okay',
  });
};

const showWarningMessage = (warningMessage: string | undefined): void => {
  Swal.fire({
    title: 'Warning!',
    text: warningMessage || 'Something went wrong, please try again.',
    icon: 'warning',
    confirmButtonText: 'Okay',
  });
};

  const handleRefreshClick = async () => {
    getFileProcessData();
  };

  const handleDownload = async () => {
    if (animalType === undefined || animalType === null || animalType === 0) {
      showWarningMessage("Please select the animal type");
      return;
    }
    const response = await DownloadBulkRegistrationTemplate(animalType);
    const data = await response.data;
    if (data != null) {
      let sliceSize = 1024;
      let byteCharacters = atob(data.data);
      let bytesLength = byteCharacters.length;
      let slicesCount = Math.ceil(bytesLength / sliceSize);
      let byteArrays = new Array(slicesCount);
      for (let sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
        let begin = sliceIndex * sliceSize;
        let end = Math.min(begin + sliceSize, bytesLength);
        let bytes = new Array(end - begin);
        for (var offset = begin, i = 0; offset < end; ++i, ++offset) {
          bytes[i] = byteCharacters[offset].charCodeAt(0);
        }
        byteArrays[sliceIndex] = new Uint8Array(bytes);
      }
      FileSaver.saveAs(
        new Blob(byteArrays, { type: "application/vnd.ms-excel" }),
        data.message
      );
    }
  };

  const handleUpload = (fileName: string) => {
    try {
      var newRecord: IFileProcessModel = {};
      newRecord.fileName = fileName;
      newRecord.status = 1;
      const newData = data.concat(newRecord);
      setData(newData);
      getFileProcessData();
    } catch (error) {
      showErrorMessage("An error has occurred. Please try again later.");
    }
  };

  const showSuccessMessage = () => {
    Swal.fire({
      title: 'Success!',
      text: `Nil Returns for ${AnimalType[animalType]} submitted.`,
      icon: 'success',
      confirmButtonText: 'Okay',
    });
  };

  const handleNilReturnsClick = async () => {
    if (animalType === undefined || animalType === null || animalType === 0) {
      showWarningMessage("Please select the animal type");
      return;
    }
    if (certificateNumber === undefined || certificateNumber === null || certificateNumber.length==0) {
      setErrorNilMessage("Please enter the Vet Centre Licence Number.");
      showWarningMessage("Please enter the Vet Centre Licence Number.");
      return;
    }    

    const result = await MySwal.fire({
      title: "Are you sure?",
      text: `Are you sure you want to submit Nil Returns for ${AnimalType[animalType]}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes!",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed)
      return;
    var bulkRegRequest: IPetRegistraionBulkRegRequest = {
      registrationType: animalType,
      vetCentreLicenceNumber : certificateNumber,
      mode: 2,
      userInformation: {
        name: user.name,
        idType: user.idType,
        idNo: user.uen,
        email: user.email,
        contactNo: user.contactNo,
        companyName: user.companyName,
        uen: user.uen,
        registeredAddress: user.registeredAddresses?.[0],
      },
      uploadedDocument: null,
    };

    setSubmiting(true);
    try {
    const response = await SaveBulkRegistrationForPets(bulkRegRequest);
    if (response.data.data) {
      setSubmiting(false);
      setErrorNilMessage("");
      showSuccessMessage();
    } else {
      setSubmiting(false);
      const errorMessage = response.data.message;
      showErrorMessage(errorMessage);
    }
  } catch (error) {
    // Handle any errors that occurred during the API call
    showErrorMessage("An error has occurred. Please try again later.");
  }
    setSubmiting(false);
    getFileProcessData();
  };

  return (
    <>
      {loginType === "CorpPass" && (
        <div>
          <h2 className="pb-2">Registration</h2>
          <div className="inst-cnt mt-4 mb-2">
            <span className="est-time">
              <i className="fas fa-clock"></i> Estimated time to complete: 15
              mins
            </span>
            <form
              className="needs-validation"
              onSubmit={handleDownload}
              noValidate
            >
              <div className="inst-cnt mt-4 mb-2">
                <div className="mb-2 reg-mode">
                  <label htmlFor="animalType" className="form-label mb-2">
                    Animal Type <span className="star">*</span>{" "}
                  </label>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="registrationType"
                      value="1"
                      id="radio1"
                      checked={animalType === 1}
                      onChange={() => {
                        setAnimalType(1);
                      }}
                      required
                    />
                    <label
                      className="form-check-label"
                      htmlFor="flexRadioDefault"
                    >
                      Cats
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="registrationType"
                      value="2"
                      id="radio2"
                      checked={animalType === 2}
                      onChange={() => {
                        setAnimalType(2);
                      }}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="flexRadioDefault1"
                    >
                      Chinchilla
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="registrationType"
                      value="3"
                      id="radio3"
                      checked={animalType === 3}
                      onChange={() => {
                        setAnimalType(3);
                      }}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="flexRadioDefault1"
                    >
                      Dogs
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="registrationType"
                      value="4"
                      id="radio4"
                      checked={animalType === 4}
                      onChange={() => {
                        setAnimalType(4);
                      }}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="flexRadioDefault1"
                    >
                      Guinea Pig
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="registrationType"
                      value="5"
                      id="radio5"
                      checked={animalType === 5}
                      onChange={() => {
                        setAnimalType(5);
                      }}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="flexRadioDefault1"
                    >
                      Pet Birds
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="registrationType"
                      value="6"
                      id="radio6"
                      checked={animalType === 6}
                      onChange={() => {
                        setAnimalType(6);
                      }}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="flexRadioDefault1"
                    >
                      Rabbit
                    </label>
                  </div>
                  {errorMessage && (
                    <p className="error-message">{errorMessage}</p>
                  )}
                </div>
                {(animalType === 1 || animalType === 3) && (<div className="mb-2 reg-mode">
                <label htmlFor="animalType" className="form-label mb-2">
                Vet Centre Licence Number <span className="star">(Mandatory only for Nil Returns)</span>{" "}
                </label>
                <input
                    type="text"
                    className="form-control"
                    id="certificateNumber"
                    placeholder="Permit/Licence Number"
                    value={certificateNumber}
                    maxLength={30}
                    onChange={(event) =>
                      setCertificateNumber(event.target.value)
                    }
                    required
                  />
                 {errorNilMessage && (
                    <p className="error-message">{errorNilMessage}</p>
                  )}
                </div>)}
              </div>
              <div className="row">
              {(animalType === 1 || animalType === 2 || animalType === 3 || animalType === 4 || animalType === 5 || animalType === 6) && (<div className="col-4">
                  <button
                    type="button"
                    onClick={handleDownload}
                    className="btn btn-success d-block mt-4"
                  >
                    Download Template
                  </button>
                </div>)}
                <div className="col-4">
                  {(animalType === 1 || animalType === 2 || animalType === 3 || animalType === 4 || animalType === 5 || animalType === 6) && showUpload && (
                    <UploadPetRegistration
                      onUpload={handleUpload}
                      registrationType={animalType}
                    ></UploadPetRegistration>
                  )}
                </div>
                <div className="col-4">
                  {(animalType === 1 || animalType === 3) && (
                    <button
                      type="button"
                      className="btn btn-success d-block mt-4"
                      onClick={handleNilReturnsClick}
                    >
                      Nil Returns
                    </button>
                  )}
                  {isSubmiting && (<Loader />)}                 
                </div>
              </div>
            </form>
          </div>
          
          <form className="row g-3 needs-validation" noValidate></form>
          <div>
            <button
              type="button"
              className="btn btn-success d-block mt-4"
              onClick={handleRefreshClick}
            >
              Refresh
            </button>
          </div>
          <FileProcessDashboard data={data}></FileProcessDashboard>
        </div>
      )}
      {loginType === "SingPass" && (
        <div className="error-message">
          You are not authorized to submit bulk registrations.
        </div>
      )}
    </>
  );
};
export default PetsBulkRegistration;
